import React, { useContext, useRef, useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
    Input,
    Row,
    Col,
    CardHeader,
    Card,
    CardBody,
    Button,
    FormGroup,
    Label
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import SweetAlert from "react-bootstrap-sweetalert";
import { UserLoginContext } from "../../context/UserLoginContext";
import rest from "../../API/api";
import { authenticatedFetch } from "../../services/authService";

const EditRegistration = () => {
    const { id: registrationId } = useParams();
    const [userState] = useContext(UserLoginContext);
    const notificationAlertRef = useRef(null);
    const history = useHistory();

    // Form state
    const [name, setName] = useState("");
    const [nameState, setNameState] = useState("");

    const [email, setEmail] = useState("");
    const [emailState, setEmailState] = useState("");

    const [dob, setDob] = useState("");
    const [dobState, setDobState] = useState("has-success");

    const [gender, setGender] = useState("Select their gender?");
    const [genderState, setGenderState] = useState("");

    const [phone, setPhone] = useState("");
    const [phoneState, setPhoneState] = useState("");

    const [signedWaiver, setSignedWaiver] = useState("Did they sign a waiver?");
    const [signedWaiverState, setSignedWaiverState] = useState("");

    const [customerXRef, setCustomerXRef] = useState("");
    const [customerXRefState, setCustomerXRefState] = useState("");

    const [notes, setNotes] = useState("");

    const [questions, setQuestions] = useState([]);
    const [questionsState, setQuestionsState] = useState("has-success");

    const [alert, setAlert] = useState(null);
    const [loading, setLoading] = useState(true);

    const [eventId, setEventId] = useState("");
    const [eventData, setEventData] = useState({});

    // Load registration data on mount
    useEffect(() => {
        const loadRegistrationData = async () => {
            try {
                const tempURL = `${rest.events}/registration/${registrationId}`;
                const response = await authenticatedFetch(tempURL, {
                    method: 'GET'
                });

                if (!response.ok) {
                    throw new Error('Failed to load registration');
                }

                const data = await response.json();

                // Populate form with existing data
                setName(data.Name || "");
                setEmail(data.Email || "");
                setDob(data.DOB || "");
                setGender(data.Gender || "Select their gender?");
                setPhone(data.Phone || "");
                setSignedWaiver(data.SignedWaiver ? "true" : "false");
                setCustomerXRef(data.CustomerXRef || "");
                setNotes(data.Notes || "");
                setQuestions(data.Questions || []);
                setEventId(data.EventId || "");

                setLoading(false);
            } catch (error) {
                console.error('Error loading registration:', error);
                notify("danger", error.message || 'Failed to load registration data.');
                setLoading(false);
            }
        };

        if (registrationId) {
            loadRegistrationData();
        }
    }, [registrationId]);

    // Notification helper
    const notify = (type, message) => {
        const options = {
            place: "tc",
            message: <div>{message}</div>,
            type: type,
            icon: "tim-icons icon-bell-55",
            autoDismiss: 5,
        };
        notificationAlertRef.current.notificationAlert(options);
    };

    // Phone number formatting
    const normalizePhone = (value, previousValue) => {
        if (!value) return value;
        const currentValue = value.replace(/[^\d]/g, '');
        const cvLength = currentValue.length;

        if (!previousValue || value.length > previousValue.length) {
            if (cvLength < 4) return currentValue;
            if (cvLength < 7) return `(${currentValue.slice(0, 3)}) ${currentValue.slice(3)}`;
            return `(${currentValue.slice(0, 3)}) ${currentValue.slice(3, 6)}-${currentValue.slice(6, 10)}`;
        }
        return value;
    };

    const handlePhoneChange = (e) => {
        const newValue = normalizePhone(e.target.value, phone);
        setPhone(newValue);

        // Validate phone
        if (newValue && newValue.length === 14) {
            setPhoneState("has-success");
        } else {
            setPhoneState("has-danger");
        }
    };

    // Email validation
    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);

        const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        if (emailRegex.test(value)) {
            setEmailState("has-success");
        } else {
            setEmailState("has-danger");
        }
    };

    // Name validation
    const handleNameChange = (e) => {
        const value = e.target.value;
        setName(value);

        if (value.length > 0) {
            setNameState("has-success");
        } else {
            setNameState("has-danger");
        }
    };

    // DOB formatting (MM/DD/YYYY)
    const normalizeDob = (value, previousValue) => {
        if (!value) return value;
        const currentValue = value.replace(/[^\d]/g, '');
        const cvLength = currentValue.length;

        if (!previousValue || value.length > previousValue.length) {
            if (cvLength < 3) return currentValue;
            if (cvLength < 5) return `${currentValue.slice(0, 2)}/${currentValue.slice(2)}`;
            return `${currentValue.slice(0, 2)}/${currentValue.slice(2, 4)}/${currentValue.slice(4, 8)}`;
        }
        return value;
    };

    // DOB validation
    const handleDobChange = (e) => {
        const newValue = normalizeDob(e.target.value, dob);
        setDob(newValue);

        if (newValue && newValue.length === 10) {
            setDobState("has-success");
        } else {
            setDobState("has-danger");
        }
    };

    // Gender validation
    const handleGenderChange = (e) => {
        const value = e.target.value;
        setGender(value);

        if (value !== "" && value !== "Select their gender?") {
            setGenderState("has-success");
        } else {
            setGenderState("has-danger");
        }
    };

    // Waiver validation
    const handleWaiverChange = (e) => {
        const value = e.target.value;
        setSignedWaiver(value);

        if (value !== "" && value !== "Did they sign a waiver?") {
            setSignedWaiverState("has-success");
        } else {
            setSignedWaiverState("has-danger");
        }
    };

    // Customer XRef validation
    const handleCustomerXRefChange = (e) => {
        const value = e.target.value;
        setCustomerXRef(value);

        if (value.length > 0) {
            setCustomerXRefState("has-success");
        } else {
            setCustomerXRefState("has-danger");
        }
    };

    // Handle question changes
    const handleQuestionChange = (e, index, isCheckbox = false) => {
        const updatedQuestions = [...questions];
        updatedQuestions[index].Value = isCheckbox ? e.target.checked.toString() : e.target.value;
        setQuestions(updatedQuestions);

        // Validate all questions have values
        const allAnswered = updatedQuestions.every(q => {
            const qType = (q.Type || "String").trim();
            if (qType === "Checkbox") {
                return q.Value === "true" || q.Value === true;
            }
            return q.Value && q.Value.trim() !== "";
        });
        if (updatedQuestions.length > 0) {
            setQuestionsState(allAnswered ? "has-success" : "has-danger");
        } else {
            setQuestionsState("has-success");
        }
    };

    // Form validation
    const isValidated = () => {
        let valid = true;

        // Name validation
        if (name === "") {
            setNameState("has-danger");
            valid = false;
        }

        // Email validation
        const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (!emailRegex.test(email)) {
            setEmailState("has-danger");
            valid = false;
        }

        // Phone validation
        if (!phone || phone.length !== 14) {
            setPhoneState("has-danger");
            valid = false;
        }

        // Customer XRef validation (always required)
        if (customerXRef === "") {
            setCustomerXRefState("has-danger");
            valid = false;
        }

        // Questions validation
        if (questions.length > 0) {
            const allAnswered = questions.every(q => q.Value && q.Value.trim() !== "");
            if (!allAnswered) {
                setQuestionsState("has-danger");
                valid = false;
            }
        }

        return valid;
    };

    // Save registration
    const saveRegistration = async () => {
        if (!isValidated()) {
            notify("warning", "Please complete all required fields.");
            return;
        }

        try {
            const tempURL = `${rest.events}/registration/${registrationId}`;

            const registrationData = {
                EventId: eventId,
                Name: name,
                Email: email,
                DOB: dob,
                Gender: gender,
                Phone: phone,
                SignedWaiver: signedWaiver === "true",
                CustomerXRef: customerXRef,
                Questions: questions,
                Processed: true,
                Notes: notes
            };

            const response = await authenticatedFetch(tempURL, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json;charset=UTF-8'
                },
                body: JSON.stringify(registrationData)
            });

            if (!response.ok) {
                throw new Error('Failed to update registration');
            }

            notify("success", "Registration updated successfully!");

            setTimeout(() => {
                history.push(`/admin/editregistrations/${eventId}`);
            }, 1000);

        } catch (error) {
            console.error('Error updating registration:', error);
            notify("danger", error.message || 'Failed to update registration.');
        }
    };

    // Save confirmation alert
    const showSaveAlert = () => {
        setAlert(
            <SweetAlert
                warning
                showCancel
                confirmBtnText="Yes, save it!"
                confirmBtnBsStyle="info"
                cancelBtnBsStyle="default"
                title="Are you sure?"
                style={{ display: "block" }}
                onConfirm={() => {
                    saveRegistration();
                    setAlert(null);
                }}
                onCancel={() => {
                    setAlert(null);
                }}
            >
                Are you sure you want to update this registration?
            </SweetAlert>
        );
    };

    if (!userState.status) {
        history.push("/admin/dashboard");
        return null;
    }

    if (loading) {
        return (
            <div className="content">
                <Row>
                    <Col md={12}>
                        <Card>
                            <CardBody>
                                <p>Loading registration data...</p>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    }

    return (
        <>
            <div className="rna-container">
                <NotificationAlert ref={notificationAlertRef} />
            </div>
            <div className="content">
                <Col md={12} className="ml-auto mr-auto">
                    <h2 className="text-center">Edit Registration</h2>
                </Col>

                {/* Registration Information Card */}
                <Card>
                    <CardHeader>Registration Information</CardHeader>
                    <CardBody>
                        <Row>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Status:</Label>
                                    <Input
                                        type="text"
                                        value="Registered"
                                        disabled
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={5}>
                                <FormGroup className={`has-label ${nameState}`}>
                                    <Label>
                                        Full Name: <span className="text-danger">*</span>
                                    </Label>
                                    <Input
                                        type="text"
                                        name="name"
                                        placeholder="ex: Mike Smith"
                                        value={name}
                                        onChange={handleNameChange}
                                    />
                                    {nameState === "has-danger" && (
                                        <label className="error">
                                            Please provide a name for the registration.
                                        </label>
                                    )}
                                </FormGroup>
                            </Col>
                            <Col md={5} mdOffset={1}>
                                <FormGroup className={`has-label ${emailState}`}>
                                    <Label>
                                        Email: <span className="text-danger">*</span>
                                    </Label>
                                    <Input
                                        type="email"
                                        name="email"
                                        placeholder="ex: dd@disney.com"
                                        value={email}
                                        onChange={handleEmailChange}
                                    />
                                    {emailState === "has-danger" && (
                                        <label className="error">
                                            Email is required and format should be <i>john@doe.com</i>.
                                        </label>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={3}>
                                <FormGroup className={`has-label ${phoneState}`}>
                                    <Label>
                                        Phone Number: <span className="text-danger">*</span>
                                    </Label>
                                    <Input
                                        type="text"
                                        name="phone"
                                        placeholder="ex: (303) 867-5309"
                                        value={phone}
                                        onChange={handlePhoneChange}
                                    />
                                    {phoneState === "has-danger" && (
                                        <label className="error">
                                            Invalid phone format. ex: (303) 867-5309
                                        </label>
                                    )}
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup className={`has-label ${customerXRefState}`}>
                                    <Label>
                                        Customer External Reference: <span className="text-danger">*</span>
                                    </Label>
                                    <Input
                                        type="text"
                                        name="customerXRef"
                                        placeholder="ex: CUST99999"
                                        value={customerXRef}
                                        onChange={handleCustomerXRefChange}
                                    />
                                    {customerXRefState === "has-danger" && (
                                        <label className="error">
                                            Please provide an external customer billing reference number.
                                        </label>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={3}>
                                <FormGroup className={`has-label ${dobState}`}>
                                    <Label>
                                        Date of Birth:
                                    </Label>
                                    <Input
                                        type="text"
                                        name="dob"
                                        placeholder="ex: MM/DD/YYYY"
                                        value={dob}
                                        onChange={handleDobChange}
                                    />
                                    {dobState === "has-danger" && (
                                        <label className="error">
                                            Please provide a date of birth.
                                        </label>
                                    )}
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup className={`has-label ${genderState}`}>
                                    <Label>
                                        Select their gender:
                                    </Label>
                                    <Input
                                        type="select"
                                        name="gender"
                                        value={gender}
                                        onChange={handleGenderChange}
                                    >
                                        <option value="Select their gender?">Select their gender?</option>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                    </Input>
                                    {genderState === "has-danger" && (
                                        <label className="error">
                                            Please provide a gender.
                                        </label>
                                    )}
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup className={`has-label ${signedWaiverState}`}>
                                    <Label>
                                        Waiver Signed:
                                    </Label>
                                    <Input
                                        type="select"
                                        name="signedWaiver"
                                        value={signedWaiver}
                                        onChange={handleWaiverChange}
                                    >
                                        <option value="Did they sign a waiver?">Did they sign a waiver?</option>
                                        <option value="true">Signed</option>
                                        <option value="false">Not Signed</option>
                                    </Input>
                                    {signedWaiverState === "has-danger" && (
                                        <label className="error">
                                            Please verify whether a waiver has been signed.
                                        </label>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={7}>
                                <FormGroup>
                                    <Label>Additional Notes</Label>
                                    <Input
                                        type="textarea"
                                        name="notes"
                                        rows="5"
                                        placeholder="Any misc or additional notes..."
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Additional Questions Card */}
                {questions.length > 0 && (
                    <Card>
                        <CardHeader>
                            Additional Questions
                            {questionsState === "has-danger" && (
                                <span className="text-danger ml-2">Please answer all questions!</span>
                            )}
                        </CardHeader>
                        <CardBody>
                            <Row>
                                <Col md={5}>
                                    {questions.map((item, index) => {
                                        const field = `question-${index}`;
                                        const questionType = (item.Type || "String").trim();

                                        // Yes/No dropdown (handle variations: Yes/No, Yes, YesNo)
                                        if (questionType === "Yes/No" || questionType === "Yes" || questionType === "YesNo") {
                                            return (
                                                <FormGroup key={field}>
                                                    <Label>
                                                        {item.Question}: <span className="text-danger">*</span>
                                                    </Label>
                                                    <Input
                                                        type="select"
                                                        name={field}
                                                        value={item.Value || ""}
                                                        onChange={(e) => handleQuestionChange(e, index)}
                                                    >
                                                        <option value="">Select...</option>
                                                        <option value="Yes">Yes</option>
                                                        <option value="No">No</option>
                                                    </Input>
                                                </FormGroup>
                                            );
                                        }

                                        // Checkbox
                                        if (questionType === "Checkbox") {
                                            return (
                                                <FormGroup key={field}>
                                                    <div className="form-check">
                                                        <Label check>
                                                            <Input
                                                                type="checkbox"
                                                                name={field}
                                                                checked={item.Value === "true" || item.Value === true}
                                                                onChange={(e) => handleQuestionChange(e, index, true)}
                                                            />
                                                            <span className="form-check-sign"></span>
                                                            {item.Question}: <span className="text-danger">*</span>
                                                        </Label>
                                                    </div>
                                                </FormGroup>
                                            );
                                        }

                                        // String (default - renders for "String" type and any unrecognized types)
                                        return (
                                            <FormGroup key={field}>
                                                <Label>
                                                    {item.Question}: <span className="text-danger">*</span>
                                                </Label>
                                                <Input
                                                    type="text"
                                                    name={field}
                                                    placeholder={item.PlaceHolder}
                                                    value={item.Value || ""}
                                                    onChange={(e) => handleQuestionChange(e, index)}
                                                />
                                            </FormGroup>
                                        );
                                    })}
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                )}

                {/* Save/Cancel Card */}
                <Card>
                    <CardHeader>Update Registration</CardHeader>
                    <CardBody>
                        <Row>
                            <Col md={2} mdOffset={4}>
                                <Button
                                    color="info"
                                    className="animation-on-hover"
                                    onClick={showSaveAlert}
                                >
                                    Update
                                </Button>
                            </Col>
                            <Col md={2}>
                                <Button
                                    color="danger"
                                    className="animation-on-hover"
                                    onClick={() => history.push(`/admin/editregistrations/${eventId}`)}
                                >
                                    Cancel
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {alert}
            </div>
        </>
    );
};

export default EditRegistration;
